import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { IS_EVENT_NAME_AVAILABLE } from "@app/graphql/events.queries";
import { sanitizeText } from "@app/lib/ui-foundation";

const DUPLICATE_EVENT_NAME_MESSAGE =
  "An event with this name already exists. Enter a unique event name.";
const EVENT_NAME_CHECK_FAILED_MESSAGE =
  "We couldn't verify this event name right now. You can still continue.";
const EVENT_NAME_CHANGED_MESSAGE =
  "The event name changed while we were checking it. Review it and try again.";

export type EventNameAvailabilityResult =
  | "available"
  | "unavailable"
  | "stale"
  | "service-error";

type EventNameForm = {
  clearErrors: (name: "eventName") => void;
  getValues: (name: "eventName") => string;
  setError: (
    name: "eventName",
    error: { type: string; message: string },
  ) => void;
};

export function useEventNameAvailability(
  form: EventNameForm,
  organizationId: string | null,
) {
  const [checkEventName] = useLazyQuery<{
    isEventNameAvailable: boolean;
  }>(IS_EVENT_NAME_AVAILABLE, { fetchPolicy: "network-only" });
  const checkSequence = useRef(0);
  const [message, setMessage] = useState<string | null>(null);

  const validateEventNameAvailability = useCallback(
    async (eventName: string): Promise<EventNameAvailabilityResult> => {
      const normalizedEventName = sanitizeText(eventName);
      if (!normalizedEventName) {
        return "available";
      }

      const currentCheckSequence = ++checkSequence.current;
      const isStale = () =>
        currentCheckSequence !== checkSequence.current ||
        sanitizeText(form.getValues("eventName")) !== normalizedEventName;

      try {
        if (!organizationId) {
          throw new Error("organizationId not available");
        }
        const { data } = await checkEventName({
          variables: { eventName: normalizedEventName, organizationId },
        });
        if (isStale()) {
          return "stale";
        }
        if (typeof data?.isEventNameAvailable !== "boolean") {
          throw new Error("Invalid event-name availability response");
        }
        if (!data.isEventNameAvailable) {
          setMessage(null);
          form.setError("eventName", {
            type: "server",
            message: DUPLICATE_EVENT_NAME_MESSAGE,
          });
          return "unavailable";
        }
        setMessage(null);
        form.clearErrors("eventName");
        return "available";
      } catch {
        if (isStale()) {
          return "stale";
        }
        setMessage(EVENT_NAME_CHECK_FAILED_MESSAGE);
        return "service-error";
      }
    },
    [checkEventName, form, organizationId],
  );

  const clearEventNameAvailability = useCallback(() => {
    checkSequence.current += 1;
    setMessage(null);
    form.clearErrors("eventName");
  }, [form]);

  const showEventNameChangedMessage = useCallback(() => {
    setMessage(EVENT_NAME_CHANGED_MESSAGE);
  }, []);

  return {
    clearEventNameAvailability,
    eventNameAvailabilityMessage: message,
    showEventNameChangedMessage,
    validateEventNameAvailability,
  };
}
